import { ReactNode } from 'react';
import { useUser } from '../context/UserContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { appConfig, appMode } from '../config';
import { ProfileCardTrigger } from './profile/ProfileCardTrigger';
import {
  RoomsIcon,
  GameIcon,
  ProfileIcon,
  BillingIcon,
  PreferencesIcon,
  LogoutIcon,
  HeartIcon,
  ChatIcon,
  CompassIcon,
} from './icons';
import './Sidebar.css';

// ==========================================
// TYPES
// ==========================================

export type View = 'discover' | 'messages' | 'rooms' | 'explore' | 'game' | 'profile' | 'connections' | 'preferences' | 'billing';

interface SidebarProps {
  currentView: View;
  onViewChange: (view: View) => void;
}

interface NavItem {
  view: View;
  icon: ReactNode;
  label: string;
}

// ==========================================
// COMPONENT
// ==========================================

export function Sidebar({ currentView, onViewChange }: SidebarProps) {
  const { user, logout } = useUser();
  const connectionsEnabled = useFeatureFlag('social.graph');
  const isClassroom = appMode === 'classroom';

  const navItems: NavItem[] = [
    { view: 'discover', icon: <HeartIcon size={22} />, label: 'Discover' },
    { view: 'messages', icon: <ChatIcon size={22} />, label: 'Messages' },
    { view: 'rooms', icon: <RoomsIcon size={22} />, label: 'Date Rooms' },
    { view: 'explore', icon: <CompassIcon size={22} />, label: 'Explore' },
    { view: 'game', icon: <GameIcon size={22} />, label: 'Game' },
    { view: 'profile', icon: <ProfileIcon size={22} />, label: 'Profile' },
    // Connections nav only appears once the social graph is enabled.
    // Dating-only entry: hidden entirely in classroom mode.
    ...(connectionsEnabled && !isClassroom
      ? [{ view: 'connections' as View, icon: <HeartIcon size={22} />, label: 'Connections' }]
      : []),
    { view: 'billing', icon: <BillingIcon size={22} />, label: 'Billing' },
    { view: 'preferences', icon: <PreferencesIcon size={22} />, label: 'Settings' },
  ];

  // Classroom feature buttons come from the preset (DM Controls, Students, Quests).
  // They forward their action to Unity the same way Header.tsx does; dating mode
  // keeps its existing nav untouched.
  const presetButtons = isClassroom ? appConfig.features.customButtons ?? [] : [];

  const handlePresetButton = (action: string) => {
    const sendUnityMessage = (window as Window & { sendUnityMessage?: (obj: string, method: string, param: string) => void }).sendUnityMessage;
    sendUnityMessage?.('UIManager', 'OnWebAction', action);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="sidebar sidebar--desktop">
        <div className="sidebar-header">
          <h1 className="logo">
            <HeartIcon size={24} className="logo-icon" />
            <span>{appConfig.name}</span>
          </h1>
        </div>

        {user && (
          <div className="user-info">
            {/* Self-profile entry point (AC-01.1) — opens your own ProfileCard.
                Self-gates on social.profile_card; renders the avatar inertly
                when off. */}
            <ProfileCardTrigger profileId={user.id}>
              <div className="user-avatar">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.displayName} />
                ) : (
                  user.displayName.charAt(0).toUpperCase()
                )}
              </div>
            </ProfileCardTrigger>
            <div className="user-details">
              <span className="user-name">{user.displayName}</span>
              <span className="user-status">Online</span>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.view}
              className={`nav-item ${currentView === item.view ? 'active' : ''}`}
              onClick={() => onViewChange(item.view)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
          {presetButtons.map((button) => (
            <button
              key={button.id}
              className="nav-item"
              onClick={() => handlePresetButton(button.action)}
            >
              <span className="nav-icon"><GameIcon size={22} /></span>
              <span className="nav-label">{button.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={logout}>
            <LogoutIcon size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav">
        {navItems.map((item) => (
          <button
            key={item.view}
            className={`mobile-nav-item ${currentView === item.view ? 'active' : ''}`}
            onClick={() => onViewChange(item.view)}
          >
            <span className="mobile-nav-icon">{item.icon}</span>
            <span className="mobile-nav-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </>
  );
}
